import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  RefreshCw,
  Loader2,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Bookmark,
  Share2,
} from "lucide-react";
import { BASE_URL } from "../../../../api/config";
import authHeader from "../../../../api/auth-header";
import { apiConfig } from "../../../../api/AxiosClient";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { healMarkdown } from "../../Chat/messages/StreamingMessage";

import useSourceSidebar from "../../Chat/hooks/useSourceSidebar";
import { getDocIdData } from "../../../../api/universal";
import { toggleThumb } from "../../../../api/feed";
import { getUserUuid } from "../../Chat/utils/auth";
import { usePopup } from "../../../contexts/PopupContext";
import ShareModal from "../../../Navigation/Sidebar/ShareModal";
import { useCompanyContext } from "../../../../context";

/* ═══════════════════════════════════════════════════════════════
   DigestArticleCard
   ═══════════════════════════════════════════════════════════════ */
const DigestArticleCard = ({
  docId,
  articleData,
  citation,
  onClickArticle,
  onShare,
  isActive,
  initialInteractions,
}) => {
  const [localLike, setLocalLike] = useState(
    initialInteractions?.like || false,
  );
  const [localDislike, setLocalDislike] = useState(
    initialInteractions?.dislike || false,
  );
  const [localCatalog, setLocalCatalog] = useState(
    initialInteractions?.catalog || false,
  );
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (initialInteractions) {
      setLocalLike(initialInteractions.like || false);
      setLocalDislike(initialInteractions.dislike || false);
      setLocalCatalog(initialInteractions.catalog || false);
    }
  }, [initialInteractions]);

  const title = articleData?.title || citation?.title || "Untitled";
  const source = articleData?.source || citation?.source_name || "";
  const targetType = articleData?.target_type || citation?.target_type || "";
  const screenshot = articleData?.screenshot || citation?.screenshot || "";
  const screenshotFull = articleData?.screenshot_full || "";
  const url = articleData?.url || citation?.url || "";
  const imgSrc = screenshot || screenshotFull;

  // Sidebar → Card sync
  useEffect(() => {
    if (!docId) return;
    const handler = (e) => {
      const d = e.detail || {};
      if (d.documentId !== docId) return;
      setLocalLike(d.like);
      setLocalDislike(d.dislike);
      setLocalCatalog(d.catalog);
    };
    window.addEventListener("shared-article:sidebar-interact", handler);
    return () =>
      window.removeEventListener("shared-article:sidebar-interact", handler);
  }, [docId]);

  // Card → Sidebar sync
  const notifySidebar = useCallback(
    (state) => {
      window.dispatchEvent(
        new CustomEvent("shared-article:card-interact", {
          detail: { documentId: docId, ...state },
        }),
      );
    },
    [docId],
  );

  const handleThumb = (action) => {
    if (action === "like") {
      const next = !localLike;
      setLocalLike(next);
      toggleThumb({ document_id: docId, action: "like" });
      let nd = localDislike;
      if (localDislike) {
        setLocalDislike(false);
        toggleThumb({ document_id: docId, action: "dislike" });
        nd = false;
      }
      notifySidebar({ like: next, dislike: nd, catalog: localCatalog });
    } else if (action === "dislike") {
      const next = !localDislike;
      setLocalDislike(next);
      toggleThumb({ document_id: docId, action: "dislike" });
      let nl = localLike;
      if (localLike) {
        setLocalLike(false);
        toggleThumb({ document_id: docId, action: "like" });
        nl = false;
      }
      notifySidebar({ like: nl, dislike: next, catalog: localCatalog });
    } else if (action === "catalog") {
      const next = !localCatalog;
      setLocalCatalog(next);
      toggleThumb({ document_id: docId, action: "catalog" });
      notifySidebar({ like: localLike, dislike: localDislike, catalog: next });
    }
  };

  const formatType = (t) =>
    t ? t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

  // Action button — round pill, active uses velBlue tint
  const actionBtn = (active) =>
    [
      "inline-flex items-center justify-center rounded-full h-6 w-6 transition-all active:scale-95 cursor-pointer border",
      active
        ? "border-velBlue/40 bg-velBlue/10"
        : "border-inputBorder bg-transparent hover:border-velBlue/30 hover:bg-velBlue/5",
    ].join(" ");

  return (
    <div
      className={[
        "rounded-lg overflow-hidden transition-all duration-150 cursor-pointer",
        "border bg-innerBg",
        isActive
          ? "border-velBlue/60 shadow-login-panel"
          : "border-inputBorder hover:border-surfaceBorder",
      ].join(" ")}
      onClick={() => onClickArticle?.(docId)}
    >
      <div className="flex gap-2.5 p-2.5 transition-colors hover:bg-velBlue/[0.02]">
        {/* Thumbnail */}
        {imgSrc && !imgError && (
          <div className="w-16 h-12 rounded overflow-hidden shrink-0 border border-inputBorder">
            <img
              src={imgSrc}
              alt=""
              className="w-full h-full object-cover object-top"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          </div>
        )}
        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-normal leading-snug line-clamp-2 text-mainText">
            {title}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            {source && (
              <span className="text-[9px] truncate max-w-[100px] text-mutedText">
                {source}
              </span>
            )}
            {source && targetType && (
              <span className="text-[9px] text-mutedText">·</span>
            )}
            {targetType && (
              <span className="text-[8px] px-1 py-0.5 rounded-full bg-velBlue/10 text-velBlue">
                {formatType(targetType)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Compact action bar */}
      <div className="flex items-center gap-1.5 px-2.5 pb-2 -mt-0.5">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleThumb("like");
          }}
          className={actionBtn(localLike)}
          aria-label="Like"
          aria-pressed={localLike}
        >
          <ThumbsUp
            className={`h-3 w-3 ${localLike ? "text-velBlue fill-velBlue" : "text-mutedText"}`}
            strokeWidth={1.5}
          />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleThumb("dislike");
          }}
          className={actionBtn(localDislike)}
          aria-label="Dislike"
          aria-pressed={localDislike}
        >
          <ThumbsDown
            className={`h-3 w-3 ${localDislike ? "text-velBlue fill-velBlue" : "text-mutedText"}`}
            strokeWidth={1.5}
          />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleThumb("catalog");
          }}
          className={actionBtn(localCatalog)}
          aria-label="Save to catalog"
          aria-pressed={localCatalog}
        >
          <Bookmark
            className={`h-3 w-3 ${localCatalog ? "text-velBlue fill-velBlue" : "text-mutedText"}`}
            strokeWidth={1.5}
          />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onShare?.(docId, {
              title,
              url,
              source,
              screenshot,
              screenshot_full: screenshotFull,
              target_type: targetType,
            });
          }}
          className={actionBtn(false)}
          aria-label="Share"
        >
          <Share2 className="h-3 w-3 text-mutedText" strokeWidth={1.5} />
        </button>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="ml-auto text-[9px] truncate max-w-[120px] text-mutedText hover:text-velBlue transition-colors"
          >
            {(() => {
              try {
                return new URL(url).hostname.replace("www.", "");
              } catch {
                return "";
              }
            })()}
          </a>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Section parser
   ═══════════════════════════════════════════════════════════════ */
function parseSections(content) {
  if (!content) return [{ md: content, trailingCites: [] }];

  const lines = content.split("\n");
  const sections = [];
  let currentLines = [];

  const flushSection = () => {
    if (currentLines.length === 0) return;
    const raw = currentLines.join("\n");

    const allCites = [];
    const seen = new Set();
    for (const m of [...raw.matchAll(/\[(\d+)\]/g)]) {
      const idx = parseInt(m[1], 10);
      if (!seen.has(idx)) {
        seen.add(idx);
        allCites.push(idx);
      }
    }

    const cleanedLines = currentLines.map((line) => {
      const stripped = line.replace(/\[(\d+)\]/g, "").trim();
      return stripped === "" && /\[\d+\]/.test(line)
        ? ""
        : line.replace(/\[(\d+)\]/g, "");
    });

    const cleanedMd = cleanedLines
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    sections.push({ md: cleanedMd, trailingCites: allCites });
    currentLines = [];
  };

  for (const line of lines) {
    if (line.trim().startsWith("## ") && currentLines.length > 0)
      flushSection();
    currentLines.push(line);
  }
  flushSection();

  return sections;
}

/* ═══════════════════════════════════════════════════════════════
   DigestMarkdown
   ═══════════════════════════════════════════════════════════════ */
const DigestMarkdown = ({ content, streaming }) => {
  const components = useMemo(
    () => ({
      p: ({ children }) => (
        <p className="text-[12px] leading-relaxed mb-2 text-mainText font-normal">
          {children}
        </p>
      ),
      h1: ({ children }) => (
        <h2 className="text-[14px] font-medium mb-2 mt-1 first:mt-0 text-mainText">
          {children}
        </h2>
      ),
      h2: ({ children }) => (
        <h3 className="text-[13px] font-medium mt-4 mb-1.5 first:mt-0 text-velBlue">
          {children}
        </h3>
      ),
      h3: ({ children }) => (
        <h4 className="text-[12px] font-medium mt-3 mb-1 first:mt-0 text-velBlue">
          {children}
        </h4>
      ),
      strong: ({ children }) => (
        <strong className="font-medium text-mainText">{children}</strong>
      ),
      em: ({ children }) => <em className="text-mutedText">{children}</em>,
      ul: ({ children }) => (
        <ul className="space-y-1 mb-3 ml-4 list-disc text-mutedText">
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className="space-y-1 mb-3 ml-4 list-decimal text-mutedText">
          {children}
        </ol>
      ),
      li: ({ children }) => (
        <li className="text-[12px] leading-relaxed text-mainText font-normal">
          {children}
        </li>
      ),
      a: ({ href, children }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-velBlue hover:text-velBlue/80 transition-colors"
        >
          {children}
        </a>
      ),
      code: ({ inline, children }) =>
        inline ? (
          <code className="px-1 py-0.5 rounded text-[11px] bg-velBlue/10 text-velBlue">
            {children}
          </code>
        ) : (
          <pre className="text-[11px] p-2 rounded my-2 overflow-x-auto bg-velBlue/5 border border-inputBorder">
            <code>{children}</code>
          </pre>
        ),
      pre: ({ children }) => <>{children}</>,
      hr: () => <hr className="my-3 border-mainDivider" />,
      blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-velBlue pl-3 my-2 italic text-[12px] text-mutedText">
          {children}
        </blockquote>
      ),
    }),
    [],
  );

  const escaped = useMemo(() => {
    if (!content) return "";
    let s = content.replace(/\[\d+\]/g, "");
    if (streaming) s = healMarkdown(s);
    return s;
  }, [content, streaming]);

  if (!content) return null;

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {escaped}
    </ReactMarkdown>
  );
};

/* ═══════════════════════════════════════════════════════════════
   DigestCardGroup
   ═══════════════════════════════════════════════════════════════ */
const DigestCardGroup = ({
  citeIndices,
  citationMap,
  cardDataMap,
  onSourceClick,
  onShare,
  activeCardId,
  citationInteractions,
}) => {
  if (!citeIndices.length) return null;

  return (
    <div className="flex flex-col gap-2 my-3">
      {citeIndices.map((idx) => {
        const citation = citationMap[idx];
        if (!citation) return null;
        const articleData = cardDataMap[citation.doc_id];

        if (!articleData) {
          return (
            <div
              key={idx}
              className="flex items-center gap-2 px-2 py-2 rounded-lg border border-inputBorder bg-velBlue/5"
            >
              <Loader2
                size={12}
                className="animate-spin shrink-0 text-velBlue/50"
              />
              <span className="text-[10px] truncate text-mutedText">
                {citation.title || "Loading..."}
              </span>
            </div>
          );
        }

        return (
          <DigestArticleCard
            key={idx}
            docId={citation.doc_id}
            articleData={articleData}
            citation={citation}
            onClickArticle={(docId) => onSourceClick?.(citation)}
            onShare={onShare}
            isActive={activeCardId === citation.doc_id}
            initialInteractions={citationInteractions?.[citation.doc_id]}
          />
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ImmediateDigest — main component
   ═══════════════════════════════════════════════════════════════ */
const ImmediateDigest = ({ onSuggestionsReady }) => {
  const [content, setContent] = useState("");
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [cardDataMap, setCardDataMap] = useState({});
  const contentRef = useRef(null);
  const abortRef = useRef(null);
  const userUuid = useMemo(() => getUserUuid(), []);
  const { openPopUp, closePopUp } = usePopup();
  const { companyKey } = useCompanyContext();

  const handleShare = useCallback(
    (docId, snapshot) => {
      openPopUp(
        <ShareModal
          onClose={closePopUp}
          contentType="article"
          documentId={docId}
          itemName={snapshot?.title || "Article"}
          articleSnapshot={snapshot}
        />,
      );
    },
    [openPopUp, closePopUp],
  );

  const messagesForSidebar = useMemo(() => {
    if (!citations.length || !content) return [];
    return [{ type: "answer", citations, answer: content }];
  }, [citations, content]);

  const {
    activeCardId,
    citationInteractions,
    handleSourceClick,
    handleSourceInteract,
  } = useSourceSidebar(messagesForSidebar);

  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      if (!d.documentId) return;
      handleSourceInteract?.(d.documentId, d.documentId, {
        like: d.like,
        dislike: d.dislike,
        catalog: d.catalog,
      });
      window.dispatchEvent(
        new CustomEvent("shared-article:sidebar-interact", {
          detail: {
            documentId: d.documentId,
            like: d.like,
            dislike: d.dislike,
            catalog: d.catalog,
          },
        }),
      );
    };
    window.addEventListener("shared-article:card-interact", handler);
    return () =>
      window.removeEventListener("shared-article:card-interact", handler);
  }, [handleSourceInteract]);

  const citationMap = useMemo(() => {
    const m = {};
    (citations || []).forEach((c) => {
      m[c.index] = c;
    });
    return m;
  }, [citations]);

  const sections = useMemo(() => {
    if (streaming || !content) return null;
    return parseSections(content);
  }, [content, streaming]);

  const cardCiteIndices = useMemo(() => {
    if (!sections) return [];
    const all = [];
    for (const s of sections) {
      for (const idx of s.trailingCites) {
        if (citationMap[idx]?.doc_id && !all.includes(idx)) all.push(idx);
      }
    }
    return all;
  }, [sections, citationMap]);

  useEffect(() => {
    if (cardCiteIndices.length === 0) return;
    for (const idx of cardCiteIndices) {
      const citation = citationMap[idx];
      if (!citation?.doc_id || cardDataMap[citation.doc_id]) continue;
      getDocIdData(citation.doc_id)
        .then((res) => {
          if (res?.data)
            setCardDataMap((prev) => ({
              ...prev,
              [citation.doc_id]: res.data,
            }));
        })
        .catch(() => {
          setCardDataMap((prev) => ({
            ...prev,
            [citation.doc_id]: {
              title: citation.title,
              url: citation.url,
              source: citation.source_name,
              target_type: citation.target_type,
            },
          }));
        });
    }
  }, [cardCiteIndices, citationMap]);

  const fetchDigest = useCallback(
    async (force = false) => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);
      if (force) {
        setContent("");
        setCitations([]);
        setCardDataMap({});
        setStreaming(true);
      }

      try {
        const headers = authHeader();
        const isDD = process.env.REACT_APP_IS_DD === "true";

        const params = new URLSearchParams();
        if (force) params.set("force", "true");
        if (isDD && apiConfig.target) params.set("target", apiConfig.target);
        const query = params.toString() ? `?${params.toString()}` : "";

        const res = await fetch(`${BASE_URL}home/digest${query}`, {
          headers,
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const contentType = res.headers.get("content-type") || "";

        if (contentType.includes("application/json")) {
          const data = await res.json();
          if (data.status === 200 && data.digest) {
            setContent(data.digest);
            setCitations(data.citations || []);
            setGeneratedAt(data.generated_at);
            // ── Lift suggestions to parent ──
            onSuggestionsReady?.({
              topics: data.suggested_topics || [],
              questions: data.suggested_questions || [],
            });
          } else {
            setError(data.message || "Failed to load digest");
          }
          setLoading(false);
        } else {
          setStreaming(true);
          setContent("");
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let fullContent = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
              if (!line.trim()) continue;
              try {
                const msg = JSON.parse(line);
                if (msg.digest_action === "stream_start" && msg.citations) {
                  setCitations(msg.citations);
                } else if (
                  msg.digest_action === "stream_chunk" &&
                  msg.content
                ) {
                  fullContent += msg.content;
                  setContent(fullContent);
                } else if (msg.digest_action === "stream_done") {
                  setGeneratedAt(msg.generated_at);
                  if (msg.citations) setCitations(msg.citations);
                  if (msg.digest) setContent(msg.digest);
                  // Suggestions may be on stream_done (JSON-style) or arrive
                  // later via suggestions_done (stream-style) — handle both
                  if (
                    msg.suggested_topics?.length ||
                    msg.suggested_questions?.length
                  ) {
                    onSuggestionsReady?.({
                      topics: msg.suggested_topics || [],
                      questions: msg.suggested_questions || [],
                    });
                  }
                } else if (msg.digest_action === "suggestions_done") {
                  onSuggestionsReady?.({
                    topics: msg.suggested_topics || [],
                    questions: msg.suggested_questions || [],
                  });
                } else if (msg.error) {
                  setError(msg.error);
                }
              } catch {
                /* skip */
              }
            }
          }
          setStreaming(false);
          setLoading(false);
        }
      } catch (e) {
        if (e.name === "AbortError") return;
        setError(e.message || "Failed to load digest");
        setLoading(false);
        setStreaming(false);
      }
    },
    [onSuggestionsReady],
  );

  useEffect(() => {
    fetchDigest(false);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchDigest, companyKey]);

  const timeAgo = useMemo(() => {
    if (!generatedAt) return "";
    const secs = Math.floor(Date.now() / 1000) - generatedAt;
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  }, [generatedAt]);

  /* ════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════ */
  return (
    <div className="flex flex-col h-full font-poppins font-normal">
      {/* ── Masthead ── */}
      <div className="shrink-0 px-4 pt-3 pb-3">
        {/* Toolbar row */}
        <div className="flex items-center justify-end mb-1">
          <div className="flex items-center gap-2">
            {generatedAt && !streaming && (
              <span className="flex items-center gap-1 text-[10px] text-mutedText">
                <Clock size={9} aria-hidden="true" />
                {timeAgo}
              </span>
            )}
            <button
              onClick={() => fetchDigest(true)}
              disabled={streaming}
              className="p-1 rounded-lg transition-colors hover:bg-btnHover text-mutedText disabled:opacity-30 cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
              title="Regenerate digest"
              aria-label="Regenerate digest"
            >
              <RefreshCw
                size={12}
                className={streaming ? "animate-spin" : ""}
                strokeWidth={2}
              />
            </button>
          </div>
        </div>

        {/* Title */}
        <div className="flex flex-col items-center">
          <h1 className="text-[26px] leading-tight tracking-tight mt-0.5 text-mainText font-light">
            Immediate Digest
          </h1>
          {/* Accent rule */}
          <div
            className="mt-2 h-[1.5px] rounded-full bg-velBlue"
            style={{ width: "60%", maxWidth: 220 }}
          />
        </div>
      </div>

      {/* ── Content ── */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto px-4 py-3"
        style={{ scrollbarWidth: "thin" }}
      >
        {loading && !content ? (
          <div className="h-full flex flex-col items-center justify-center gap-2 text-mutedText">
            <Loader2 size={18} className="animate-spin text-velBlue" />
            <span className="text-[11px]">Generating your digest…</span>
          </div>
        ) : error && !content ? (
          <div className="h-full flex flex-col items-center justify-center gap-2">
            <span className="text-[11px] text-errorText">{error}</span>
            <button
              onClick={() => fetchDigest(true)}
              className="text-[10px] px-2 py-1 rounded-lg border border-velBlue/30 text-velBlue transition-colors hover:bg-velBlue/10 cursor-pointer"
            >
              Try again
            </button>
          </div>
        ) : streaming ? (
          <>
            <DigestMarkdown content={content} streaming={true} />
            {/* Streaming cursor */}
            <span
              className="inline-block w-1.5 h-3.5 ml-0.5 rounded-sm animate-pulse bg-velBlue"
              aria-hidden="true"
            />
          </>
        ) : sections ? (
          sections.map((section, i) => (
            <div key={i}>
              <DigestMarkdown content={section.md} streaming={false} />
              {section.trailingCites.length > 0 && (
                <DigestCardGroup
                  citeIndices={section.trailingCites}
                  citationMap={citationMap}
                  cardDataMap={cardDataMap}
                  userUuid={userUuid}
                  onSourceClick={handleSourceClick}
                  onShare={handleShare}
                  activeCardId={activeCardId}
                  citationInteractions={citationInteractions}
                />
              )}
            </div>
          ))
        ) : (
          <DigestMarkdown content={content} streaming={false} />
        )}
      </div>
    </div>
  );
};

export default ImmediateDigest;
